import PropTypes from 'prop-types';
import { Grid, TextField, Switch, FormControlLabel, Typography, Box } from '@material-ui/core';

export function getLifecycleValues(lifecycle) {
  const inactivity = lifecycle?.inactivity_policy;
  return {
    shutdownEnabled: inactivity?.shutdown_policy?.enabled ?? false,
    shutdownWindowDays: inactivity?.shutdown_policy?.window_days ?? 1,
    deletionEnabled: inactivity?.deletion_policy?.enabled ?? false,
    deletionWindowDays: inactivity?.deletion_policy?.window_days ?? 1
  };
}

export function buildLifecyclePayload(values) {
  return {
    inactivity_policy: {
      shutdown_policy: {
        enabled: values.shutdownEnabled,
        window_days: parseInt(values.shutdownWindowDays, 10)
      },
      deletion_policy: {
        enabled: values.deletionEnabled,
        window_days: parseInt(values.deletionWindowDays, 10)
      }
    }
  };
}

LifecycleEditor.propTypes = {
  values: PropTypes.shape({
    shutdownEnabled: PropTypes.bool,
    shutdownWindowDays: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    deletionEnabled: PropTypes.bool,
    deletionWindowDays: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  isEditing: PropTypes.bool.isRequired
};

export default function LifecycleEditor({ values, onChange, isEditing }) {
  const handleWindowDaysChange = (field) => (event) => {
    const { value } = event.target;
    // Allow only positive integers between 1-365
    if (
      value === '' ||
      (/^\d+$/.test(value) && parseInt(value, 10) >= 1 && parseInt(value, 10) <= 365)
    ) {
      onChange({ ...values, [field]: value === '' ? '' : parseInt(value, 10) });
    }
  };

  const handleSwitchChange = (field) => (event) => {
    const newValues = { ...values, [field]: event.target.checked };
    // If shutdown is being disabled, also disable deletion
    if (field === 'shutdownEnabled' && !event.target.checked) {
      newValues.deletionEnabled = false;
    }
    onChange(newValues);
  };

  const getHelperText = (isEnabled, windowDays, label) => {
    if (!isEditing) return '';
    if (isEnabled && windowDays === '') return `Required Field`;
    return label;
  };

  return (
    <Grid container rowSpacing={3}>
      {/* Shutdown Policy Section */}
      <Grid item xs={12}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Shutdown Policy
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={
              <Switch
                checked={values.shutdownEnabled}
                onChange={handleSwitchChange('shutdownEnabled')}
                disabled={!isEditing}
              />
            }
            label={values.shutdownEnabled ? 'Enabled' : 'Disabled'}
          />
          <TextField
            label="Window Days"
            type="number"
            value={values.shutdownWindowDays}
            onChange={handleWindowDaysChange('shutdownWindowDays')}
            disabled={!isEditing || !values.shutdownEnabled}
            size="small"
            inputProps={{ min: 1, max: 365 }}
            sx={{ width: 200 }}
            error={values.shutdownEnabled && values.shutdownWindowDays === ''}
            helperText={getHelperText(
              values.shutdownEnabled,
              values.shutdownWindowDays,
              'Days before shutdown (1-365)'
            )}
          />
        </Box>
      </Grid>

      {/* Deletion Policy Section */}
      <Grid item xs={12}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Deletion Policy
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={
              <Switch
                checked={values.deletionEnabled}
                onChange={handleSwitchChange('deletionEnabled')}
                disabled={!isEditing || !values.shutdownEnabled}
              />
            }
            label={values.deletionEnabled ? 'Enabled' : 'Disabled'}
          />
          <TextField
            label="Window Days"
            type="number"
            value={values.deletionWindowDays}
            onChange={handleWindowDaysChange('deletionWindowDays')}
            disabled={!isEditing || !values.deletionEnabled || !values.shutdownEnabled}
            size="small"
            inputProps={{ min: 1, max: 365 }}
            sx={{ width: 200 }}
            error={values.deletionEnabled && values.deletionWindowDays === ''}
            helperText={getHelperText(
              values.deletionEnabled,
              values.deletionWindowDays,
              'Days before deletion (1-365)'
            )}
          />
        </Box>
      </Grid>
    </Grid>
  );
}
